import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { checkUpdateUser, updateUser } from 'api/users'
import FormCheckbox from 'components/form-elements/FormCheckbox'
import FormDatePicker from 'components/form-elements/FormDatePicker'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import FormSelect from 'components/form-elements/FormSelect'
import ConfirmModal from 'components/modals/ConfirmModal'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID } from 'const'
import { CohortsMultiSelect } from 'features/Cohorts'
import { useUnlicensedConfirm } from 'features/User/hooks/useUnlicensedConfirm'
import { UserPasswordSetupLink } from '../UserPasswordSetupLink'
import {
  getPathwayNameById,
  isFamilyCaregiverPathway,
  isNotEnglishPathway,
} from 'models/pathways.model'
import {
  educationOptions,
  highestDegreeOptions,
  preferredLanguageOptions,
  racialDesignationOptions,
  sexOptions,
  yearsOfExperience,
} from 'models/user.model'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import {
  AccessCodeDropdownItem,
  EAccessCodeOwnership,
  EUserEducation,
  EUserExperience,
  EUserHighestDegree,
  EUserLanguage,
  EUserRacialDesignation,
  EUserSex,
  Nullish,
  Pathway,
  TableResponse,
  UserModel,
  UserUpdateRequest,
} from 'types'
import { any, boolean, coerce, literal, number, object, string, TypeOf, ZodIssueCode } from 'zod'

const formSchema = object({
  firstname: string().min(1, FIELD_IS_REQUIRED),
  lastname: string().min(1, FIELD_IS_REQUIRED),
  email: string().email(EMAIL_IS_INVALID),
  cohorts: object({
    label: string(),
    value: number(),
  }).array(),
  customTrainingPathwayTypeId: coerce.number(),
  education: string().min(1, 'Education is required'),
  experience: string().min(1, FIELD_IS_REQUIRED),
  language: string().min(1, FIELD_IS_REQUIRED),
  highestDegree: string(),
  otherHighestDegree: string(),
  racialDesignation: string(),
  otherRacialDesignation: string(),
  surveyMonkeySex: string(),
  otherSurveyMonkeySex: string(),
  surveyMonkeyAge: coerce.number().optional(),
  surveyMonkeyDate: any().optional().nullable(),
  jobTitle: string().max(50, 'Maximum length is 50 characters'),
  accessCodeId: string(),
  shouldLeaveOrganization: boolean(),
  phoneNumber: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
  isEmailNotificationEnabled: boolean(),
  isSmsNotificationEnabled: boolean(),
}).superRefine((data, refinementContext) => {
  if (data.highestDegree === EUserHighestDegree.other && data.otherHighestDegree === '') {
    refinementContext.addIssue({
      code: ZodIssueCode.custom,
      message: FIELD_IS_REQUIRED,
      path: ['otherHighestDegree'],
    })
  }

  if (
    data.racialDesignation === EUserRacialDesignation.other &&
    data.otherRacialDesignation === ''
  ) {
    refinementContext.addIssue({
      code: ZodIssueCode.custom,
      message: FIELD_IS_REQUIRED,
      path: ['otherRacialDesignation'],
    })
  }

  if (data.surveyMonkeySex === EUserSex.other && data.otherSurveyMonkeySex === '') {
    refinementContext.addIssue({
      code: ZodIssueCode.custom,
      message: FIELD_IS_REQUIRED,
      path: ['otherSurveyMonkeySex'],
    })
  }

  if (data.isSmsNotificationEnabled && (!data.phoneNumber || data.phoneNumber.length < 10)) {
    refinementContext.addIssue({
      path: ['phoneNumber'],
      code: ZodIssueCode.custom,
      message: FIELD_IS_REQUIRED,
    })
  }
})

type FormType = TypeOf<typeof formSchema>

export function UserDetailsForm({
  user,
  pathways,
  accessCodes,
  onAfterUpdate,
}: {
  user: UserModel
  pathways: TableResponse<Pathway>
  accessCodes: AccessCodeDropdownItem[]
  onAfterUpdate: () => void
}) {
  const [checkResult, setCheckResult] = useState<Nullish<UserUpdateRequest>>(null)

  const defaultValues = {
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    cohorts: user.cohorts.map((cohort) => ({ label: cohort.name, value: cohort.id })),
    education: user.education,
    experience: user.experience,
    language: user.language,
    highestDegree: user.highestDegree || '',
    otherHighestDegree: user.otherHighestDegree || '',
    racialDesignation: user.racialDesignation || '',
    otherRacialDesignation: user.otherRacialDesignation || '',
    surveyMonkeySex: user.surveyMonkeySex || '',
    otherSurveyMonkeySex: user.otherSurveyMonkeySex || '',
    jobTitle: user.jobTitle || '',
    customTrainingPathwayTypeId: user.trainingPathwayTypeId,
    accessCodeId: user.isUnlicensed ? 'null' : String(user.accessCodeId),
    shouldLeaveOrganization: !!user.leftOrganizationAt,
    phoneNumber: user.phoneNumber,
    isEmailNotificationEnabled: user.isEmailNotificationEnabled || false,
    isSmsNotificationEnabled: user.isSmsNotificationEnabled || false,
    surveyMonkeyAge: user.surveyMonkeyAge || undefined,
    surveyMonkeyDate: user.surveyMonkeyDate || null,
  }

  const methods = useForm<FormType>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const emailValue = methods.watch('email')
  const isEmailDirty = emailValue !== user.email
  const highestDegreeValue = methods.watch('highestDegree')
  const racialDesignationValue = methods.watch('racialDesignation')
  const surveyMonkeySexValue = methods.watch('surveyMonkeySex')
  const phoneNumber = methods.watch('phoneNumber')

  const smsWarningText = phoneNumber
    ? 'Agreement has been given to receive sms. User is informed that they can opt out by sending STOP'
    : 'Please enter mobile phone number.\n Agreement has been given to receive sms. User is informed that they can opt out by sending STOP'

  const checkUserMutation = useMutation(checkUpdateUser)
  const saveUserMutation = useMutation(updateUser)

  const getRequestData = useCallback(() => {
    const values = methods.getValues()

    const customTrainingPathwayTypeId = Number(values.customTrainingPathwayTypeId)
    let language = values.language as EUserLanguage
    if (
      isNotEnglishPathway(customTrainingPathwayTypeId) &&
      values.language === EUserLanguage.english
    ) {
      language = EUserLanguage.other
    }

    const accessCodeId =
      values.accessCodeId === 'null' ? user.accessCodeId : parseInt(values.accessCodeId)

    const data: UserUpdateRequest = {
      id: user.id,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      cohortIds: values.cohorts.map((item) => item.value),
      education: values.education as EUserEducation,
      experience: values.experience as EUserExperience,
      language,
      highestDegree: values.highestDegree as EUserHighestDegree,
      racialDesignation: values.racialDesignation as EUserRacialDesignation,
      surveyMonkeySex: values.surveyMonkeySex as EUserSex,
      jobTitle: values.jobTitle,
      customTrainingPathwayTypeId,
      accessCodeId,
      shouldLeaveOrganization: values.shouldLeaveOrganization,
      isUnlicensed: values.accessCodeId === 'null',
      phoneNumber: values.phoneNumber,
      isEmailNotificationEnabled: values.isEmailNotificationEnabled,
      isSmsNotificationEnabled: values.isSmsNotificationEnabled,
      surveyMonkeyAge: values.surveyMonkeyAge || undefined,
      surveyMonkeyDate: values.surveyMonkeyDate
        ? moment(values.surveyMonkeyDate).toISOString()
        : undefined,
    }

    if (values.highestDegree === EUserHighestDegree.other) {
      data.otherHighestDegree = values.otherHighestDegree
    }

    if (values.racialDesignation === EUserRacialDesignation.other) {
      data.otherRacialDesignation = values.otherRacialDesignation
    }

    if (values.surveyMonkeySex === EUserSex.other) {
      data.otherSurveyMonkeySex = values.otherSurveyMonkeySex
    }

    return data
  }, [methods, user.id, user.accessCodeId])

  const getPathwayName = useCallback(
    (pathwayTypeId?: number) => {
      return getPathwayNameById(pathways, pathwayTypeId)
    },
    [pathways],
  )

  const [isOpenConfirm, setIsOpenConfirm] = useState(false)
  const handleCloseConfirm = useCallback(() => setIsOpenConfirm(false), [])

  const confirmMessage = useMemo(() => {
    if (!checkResult) {
      return ''
    }

    const oldName = getPathwayName(user.trainingPathwayTypeId)
    const request = getRequestData()
    const newName = getPathwayName(Number(request.customTrainingPathwayTypeId))

    return `After saving these changes the trainee's pathway type will be changed from ${oldName} to ${newName}. Please confirm the action.`
  }, [checkResult, getPathwayName, getRequestData, user])

  const handleUpdate = useCallback(async () => {
    const request = getRequestData()
    await saveUserMutation.mutateAsync(request)
    handleCloseConfirm()
    NotificationSys.showSuccess('User updated')
    onAfterUpdate()
  }, [getRequestData, handleCloseConfirm, onAfterUpdate, saveUserMutation])

  const { unlicensedConfirm, openUnlicensedConfirm } = useUnlicensedConfirm()

  const handleCheckUpdate = useCallback(async () => {
    const request = getRequestData()

    if (request.isUnlicensed && !!user.accessCode?.id && !user.isUnlicensed) {
      if (!(await openUnlicensedConfirm())) {
        return
      }
    }

    const isChangedPathway = request.customTrainingPathwayTypeId !== user.trainingPathwayTypeId
    if (isChangedPathway) {
      setCheckResult(request)
      setIsOpenConfirm(true)
    } else {
      handleUpdate()
    }
  }, [getRequestData, handleUpdate, user, openUnlicensedConfirm])

  const pathwayOptions = useMemo(() => {
    // We should not show Family Caregivers pathway in the dropdown,
    const traineePathways = pathways.data.rows.filter((pathwayItem) => {
      return !isFamilyCaregiverPathway(pathwayItem.trainingPathwayTypeId)
    })

    return traineePathways.map((pathwayItem) => {
      return {
        label: pathwayItem.name,
        value: pathwayItem.trainingPathwayTypeId,
      }
    })
  }, [pathways])

  const isLanguageDisabled = useMemo(() => {
    return !isNotEnglishPathway(user.trainingPathwayTypeId)
  }, [user])

  const languageOptions = useMemo(() => {
    if (isNotEnglishPathway(user.trainingPathwayTypeId)) {
      return preferredLanguageOptions.filter((item) => item.value !== EUserLanguage.english)
    }

    return preferredLanguageOptions
  }, [user])

  const accessCodeOptions = useMemo(() => {
    // Individual codes are single-seat and belong to one trainee — offering them here would let
    // an admin move somebody else's trainee onto a personal licence. The user's own code is still
    // added below, so the current value never disappears from the select.
    const options = accessCodes
      .filter(({ ownership }) => ownership !== EAccessCodeOwnership.individual)
      .map(({ id, code, specificState, specificUnitName }) => ({
        value: id,
        label: `${specificState}/${specificUnitName}/${code}`,
      }))

    if (
      user.accessCode &&
      !user.isUnlicensed &&
      !options.find((o) => o.value === user.accessCode?.id)
    ) {
      const { id, code, specificState, specificUnitName } = user.accessCode
      options.unshift({
        value: id,
        label: `${specificState}/${specificUnitName}/${code}`,
      })
    }

    return [
      {
        value: 'null',
        label: 'Unlicensed',
      },
      ...options,
    ]
  }, [accessCodes, user.isUnlicensed, user.accessCode])

  return (
    <>
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(handleCheckUpdate)}
          noValidate
          width="100%"
          mt={4}
        >
          <Grid container spacing={4}>
            <Grid item sm={12} md={12} lg={6} xl={6}>
              <Stack spacing={2}>
                <FormInput label="First Name" name="firstname" required fullWidth />
                <FormInput label="Last Name" name="lastname" required fullWidth />
                <FormInput label="Email" name="email" required fullWidth autoComplete="off" />

                <CohortsMultiSelect defaultValue={defaultValues.cohorts} />

                <FormSelect
                  label="State/Unit"
                  name="accessCodeId"
                  required={!user.leftOrganizationAt}
                  options={accessCodeOptions}
                  disabled={!!user.leftOrganizationAt}
                  maxRows={5}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                />
                <FormCheckbox
                  name="shouldLeaveOrganization"
                  label="Left the Organization"
                  confirmText="Please confirm that the trainee has left the organization."
                  confirmChangeToTrue={!user.leftOrganizationAt}
                  disabled={!!user.leftOrganizationAt}
                />
              </Stack>

              <Stack mt={4}>
                <Typography fontWeight={600}>Pathway changes</Typography>
                <Typography fontSize={14} sx={{ opacity: '0.6' }}>
                  Please note that modifying the fields below may potentially alter the pathway for
                  trainees
                </Typography>
              </Stack>

              <Stack spacing={2} mt={2}>
                <FormSelect
                  label="Pathway Type"
                  name="customTrainingPathwayTypeId"
                  required
                  options={pathwayOptions}
                />

                <FormSelect
                  label="How much education did you complete?"
                  name="education"
                  required
                  disabled
                  options={educationOptions}
                />

                <FormSelect
                  label="Years of experience as a professional caregiver"
                  name="experience"
                  required
                  disabled
                  options={yearsOfExperience}
                />

                <FormSelect
                  label="What is your preferred language?"
                  name="language"
                  required
                  disabled={isLanguageDisabled}
                  options={languageOptions}
                />
              </Stack>
            </Grid>

            <Grid item sm={12} md={12} lg={6} xl={6}>
              <Stack spacing={2}>
                <FormSelect
                  label="Survey Monkey - Highest degree or level of education"
                  name="highestDegree"
                  options={highestDegreeOptions}
                />

                {highestDegreeValue === EUserHighestDegree.other && (
                  <FormInput
                    label=""
                    name="otherHighestDegree"
                    required
                    fullWidth
                    checkTouchField={false}
                  />
                )}

                <FormSelect
                  label="Survey Monkey - Racial designations"
                  name="racialDesignation"
                  options={racialDesignationOptions}
                />

                {racialDesignationValue === EUserRacialDesignation.other && (
                  <FormInput
                    label=""
                    name="otherRacialDesignation"
                    required
                    fullWidth
                    checkTouchField={false}
                  />
                )}

                <FormInput
                  label="Survey Monkey - Age"
                  name="surveyMonkeyAge"
                  fullWidth
                  type="number"
                  inputProps={{ step: 1, min: 1 }}
                />

                <FormSelect
                  label="Survey Monkey - Sex"
                  name="surveyMonkeySex"
                  options={sexOptions}
                />

                {surveyMonkeySexValue === EUserSex.other && (
                  <FormInput
                    label=""
                    name="otherSurveyMonkeySex"
                    required
                    fullWidth
                    checkTouchField={false}
                  />
                )}

                <FormDatePicker label="Survey Monkey - Date" name="surveyMonkeyDate" />

                <FormInput label="Job title" name="jobTitle" fullWidth />
                <FormPhone label="Mobile phone number" name="phoneNumber" fullWidth />
              </Stack>

              <Stack mt={4}>
                <Typography fontWeight={600}>Notification Settings</Typography>
                <Typography fontSize={14} sx={{ opacity: '0.6' }}>
                  Receive reminders Weekly
                </Typography>
              </Stack>

              <Stack flexDirection="row">
                <FormCheckbox name="isEmailNotificationEnabled" label="Email" />
                <FormCheckbox
                  name="isSmsNotificationEnabled"
                  label="Text messages"
                  warningText={smsWarningText}
                />
              </Stack>
            </Grid>

            <Grid item sm={12} md={12} lg={12} xl={12}>
              <LoadingButton
                disabled={saveUserMutation.isLoading || checkUserMutation.isLoading}
                loading={saveUserMutation.isLoading || checkUserMutation.isLoading}
                variant="contained"
                type="submit"
                sx={{ width: '120px' }}
              >
                Save
              </LoadingButton>
            </Grid>

            {/* Trainees registered with a placeholder email cannot use Forgot Password — the admin
                hands them a setup link instead. Shared with the admin / org user forms. */}
            <Grid item sm={12} md={12} lg={12} xl={12}>
              <Stack spacing={2} alignItems="flex-start">
                <UserPasswordSetupLink
                  userId={user.id}
                  disabled={isEmailDirty || saveUserMutation.isLoading}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {isOpenConfirm && (
          <ConfirmModal
            title="Confirm Pathway Changes"
            body={confirmMessage}
            handleClose={handleCloseConfirm}
            handleConfirm={handleUpdate}
            loading={saveUserMutation.isLoading}
          />
        )}

        {unlicensedConfirm}
      </FormProvider>
    </>
  )
}
